// Curated, case-insensitive color source for produce grades.
//
// Mirrors the commodity-pill pattern used across Customer Specs / Package Index:
// returns { bg, border } so it can drive the existing Pill / SizeTag styled
// components directly. Backgrounds are dark/saturated so white text stays legible.
//
// Lookup is case-insensitive — grade names are stored upper-cased in some specs
// and title-cased in others, so 'CHOICE', 'Choice', and 'choice' must resolve to
// one color. Any grade not in the curated map falls back to a deterministic HSL
// color (65% sat / 35% light, white-text-legible) — the same fallback the
// commodity pills use, so a given name always renders the same hue across reloads.
//
// All current grade names (Grades DataTable, 2026-05) are curated below. A newly
// created grade needs no code change — it falls through to the legible hash
// fallback (below) and gets a stable color automatically.

const GRADE_COLORS = {
  // Quality tiers
  FANCY:        { bg: '#2563EB', border: '#1D4ED8' }, // blue
  CHOICE:       { bg: '#7C3AED', border: '#5F2CB8' }, // violet
  JUICE:        { bg: '#B45309', border: '#8A3F07' }, // amber
  STANDARD:     { bg: '#0F766E', border: '#0B5C55' }, // teal
  COMBO:        { bg: '#BE123C', border: '#9A0F31' }, // crimson
  MIXED:        { bg: '#4338CA', border: '#3530A1' }, // indigo
  'BULK BIN':   { bg: '#15803D', border: '#106331' }, // green
  WASTE:        { bg: '#78350F', border: '#5C280B' }, // dark brown
  // Export (EXP) variants
  'EXP FANCY':  { bg: '#0369A1', border: '#075985' }, // cyan-blue
  'EXP CHOICE': { bg: '#86198F', border: '#6B1372' }, // fuchsia
  'EXP COMBO':  { bg: '#9F1239', border: '#7E0E2E' }, // dark crimson
  // HBE variants
  'HBE FANCY':  { bg: '#1E40AF', border: '#18348C' }, // royal navy
  'HBE CHOICE': { bg: '#5B21B6', border: '#491A92' }, // deep purple
  // Other
  '#1 MIXED':   { bg: '#3730A3', border: '#2C2682' }, // deep indigo
  'GRADE OUTS': { bg: '#BE185D', border: '#98134A' }, // pink-magenta
};

// ALL grades selected on an entry, OR no grades at all → neutral grey (matches
// today's SizeTag). Single source of truth for the per-entry assignment pill's
// grey state (M4/M6). Spec-level grade chips always use a real color.
export const GRADE_NEUTRAL = { bg: '#555', border: '#444' };

// Deterministic, white-text-legible fallback for any uncurated grade name.
const hashColor = (name) => {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return { bg: `hsl(${hue} 65% 35%)`, border: `hsl(${hue} 70% 25%)` };
};

// Canonical key for both the case-insensitive map lookup and a stable hash.
const canon = (name) => String(name == null ? '' : name).trim().toUpperCase();

export function gradeColor(name) {
  const key = canon(name);
  return GRADE_COLORS[key] || hashColor(key);
}

export default gradeColor;
